import { DateTime } from "luxon";

const DATE_FORMATS = [
  "yyyy-MM-dd",
  "M-d-yyyy",
  "MM-dd-yyyy",
  "M/d/yyyy",
  "MM/dd/yyyy",
  "MMM d, yyyy",
  "MMMM d, yyyy",
  "MMM dd, yyyy",
  "MMMM dd, yyyy",
];

function blank(value) {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

function parseDateOnly(value) {
  if (blank(value)) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error("Invalid date");
    // XLSX cellDates are interpreted as spreadsheet calendar dates.
    return DateTime.fromJSDate(value).toFormat("yyyy-MM-dd");
  }
  const text = String(value).trim();
  for (const format of DATE_FORMATS) {
    const parsed = DateTime.fromFormat(text, format, {
      zone: "UTC",
      locale: "en-US",
      numberingSystem: "latn",
    });
    if (parsed.isValid) return parsed.toFormat("yyyy-MM-dd");
  }

  // For ISO/SQL datetime strings, validate the entire timestamp before
  // retaining its calendar date and discarding its time.

  const sqlOrIso = /^(\d{4}-\d{2}-\d{2})[ T]/.exec(text);

  if (sqlOrIso) {
    let timestamp = DateTime.fromSQL(text, { zone: "UTC" });
    if (!timestamp.isValid) {
      timestamp = DateTime.fromISO(text, { zone: "UTC", setZone: true });
    }
    if (!timestamp.isValid) throw new Error(`Invalid date/time: ${text}`);
    const date = DateTime.fromFormat(sqlOrIso[1], "yyyy-MM-dd", {
      zone: "UTC",
    });
    if (date.isValid) return date.toFormat("yyyy-MM-dd");
  }
  throw new Error(`Invalid date: ${text}`);
}

export function formatDateOnly(value) {
  if (blank(value)) return "";
  return DateTime.fromFormat(parseDateOnly(value), "yyyy-MM-dd", {
    zone: "UTC",
  }).toFormat("MM-dd-yyyy");
}

export function formatTimestampUtc(value) {
  if (blank(value)) return "";

  let parsed;

  if (value instanceof Date) {
    parsed = DateTime.fromJSDate(value, { zone: "UTC" }).toUTC();
  } else {
    const text = String(value).trim();

    // The backend may already return a formatted timestamp:
    // MM-dd-yyyy HH:mm:ss. Preserve its value instead of parsing it as ISO.
    const formattedTimestamp = DateTime.fromFormat(
      text,
      "MM-dd-yyyy HH:mm:ss",
      {
        zone: "UTC",
        locale: "en-US",
        numberingSystem: "latn",
        setZone: true,
      },
    );

    if (formattedTimestamp.isValid) {
      return formattedTimestamp.toFormat("MM-dd-yyyy HH:mm:ss");
    }

    // Support SQL datetime strings.
    parsed = DateTime.fromSQL(text, { zone: "UTC" });

    // Support ISO timestamps, including explicit offsets.
    if (!parsed.isValid) {
      parsed = DateTime.fromISO(text, {
        zone: "UTC",
        setZone: true,
      }).toUTC();
    }
  }

  if (!parsed || !parsed.isValid) return "";

  return parsed.toUTC().toFormat("MM-dd-yyyy HH:mm:ss");
}
